export type StyleTemplateThemeMode = 'lite' | 'dark';

export type StyleTemplateThemeCarrier = {
  themeMode?: StyleTemplateThemeMode | string | null;
};

export type StyleTemplatesActiveIds = {
  activeTemplateId?: number | null;
  activeLiteTemplateId?: number | null;
  activeDarkTemplateId?: number | null;
};

export function normalizeStyleTemplateThemeMode(value: unknown): StyleTemplateThemeMode {
  return value === 'dark' ? 'dark' : 'lite';
}

export function hasExplicitTemplateThemeMode(template: StyleTemplateThemeCarrier | null | undefined): boolean {
  const raw = template?.themeMode;
  return raw === 'lite' || raw === 'dark';
}

/** Resolved mode when one is stored on the template; undefined for legacy templates. */
export function getExplicitTemplateThemeMode(
  template: StyleTemplateThemeCarrier | null | undefined,
): StyleTemplateThemeMode | undefined {
  const raw = template?.themeMode;
  if (raw === 'dark' || raw === 'lite') return raw;
  return undefined;
}

export function getTemplateThemeMode(template: StyleTemplateThemeCarrier | null | undefined): StyleTemplateThemeMode {
  return normalizeStyleTemplateThemeMode(template?.themeMode);
}

export function resolveActiveLiteTemplateId(data: StyleTemplatesActiveIds | null | undefined): number | null {
  const fromLite = data?.activeLiteTemplateId;
  if (fromLite != null) return Number(fromLite);
  const legacy = data?.activeTemplateId;
  return legacy != null ? Number(legacy) : null;
}

export function resolveActiveDarkTemplateId(data: StyleTemplatesActiveIds | null | undefined): number | null {
  const fromDark = data?.activeDarkTemplateId;
  return fromDark != null ? Number(fromDark) : null;
}

export function filterTemplatesByThemeMode<T extends StyleTemplateThemeCarrier>(
  templates: T[],
  mode: StyleTemplateThemeMode,
): T[] {
  return templates.filter((t) => {
    const explicit = getExplicitTemplateThemeMode(t);
    if (!explicit) return true;
    return explicit === mode;
  });
}

export function buildActiveThemeTemplateOptions<T extends { id: number; name: string } & StyleTemplateThemeCarrier>(
  templates: T[],
  mode: StyleTemplateThemeMode,
): { label: string; value: number }[] {
  return filterTemplatesByThemeMode(templates, mode).map((t) => ({
    label: t.name,
    value: Number(t.id),
  }));
}

export function formatTemplateThemeLabel(mode: StyleTemplateThemeMode): string {
  return mode === 'dark' ? 'Dark' : 'Lite';
}

export const TEMPLATE_THEME_MODE_FORM_OPTIONS: { label: string; value: StyleTemplateThemeMode }[] = [
  { label: formatTemplateThemeLabel('lite'), value: 'lite' },
  { label: formatTemplateThemeLabel('dark'), value: 'dark' },
];

export const templateThemeModeFormRules = [
  { required: true, message: 'Select template mode (lite or dark)' },
];

export function formatTemplateSelectLabel(
  template: { id: number; name: string } & StyleTemplateThemeCarrier,
  options?: {
    activeLiteTemplateId?: number | null;
    activeDarkTemplateId?: number | null;
    showThemeMode?: boolean;
  },
): string {
  const mode = getExplicitTemplateThemeMode(template);
  const parts = [template.name];
  if (options?.showThemeMode !== false && mode === 'dark') {
    parts.push('(dark)');
  }
  const id = Number(template.id);
  const activeBits: string[] = [];
  if (options?.activeLiteTemplateId != null && Number(options.activeLiteTemplateId) === id) {
    activeBits.push('active lite');
  }
  if (options?.activeDarkTemplateId != null && Number(options.activeDarkTemplateId) === id) {
    activeBits.push('active dark');
  }
  if (activeBits.length) {
    parts.push(`(${activeBits.join(', ')})`);
  }
  return parts.join(' ');
}
